import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

import type { SearchFilters, AdWithBrand, AdspyIndustry, AdspyLanguage } from '@/types/adspy';

interface SearchParams {
  filters: SearchFilters;
  enabled?: boolean;
}

export function useAdspySearch({ filters, enabled = true }: SearchParams) {
  return useInfiniteQuery({
    queryKey: ['adspy-search', filters],
    queryFn: async ({ pageParam }) => {
      // Build query on local database with filters
      let query = supabase
        .from('v_ads_with_brands')
        .select('*')
        .order('ad_delivery_start_time', { ascending: false })
        .limit(24);

      if (pageParam) {
        query = query.lt('ad_delivery_start_time', pageParam);
      }

      if (filters.searchTerm) {
        query = query.or(`ad_creative_body.ilike.%${filters.searchTerm}%,page_name.ilike.%${filters.searchTerm}%,brand_name.ilike.%${filters.searchTerm}%`);
      }

      if (filters.format && filters.format !== 'ALL') {
        const formatLower = filters.format.toLowerCase() as 'image' | 'video' | 'carousel';
        query = query.eq('format', formatLower);
      }

      if (filters.status && filters.status !== 'ALL') {
        query = query.eq('is_active', filters.status === 'ACTIVE');
      }

      if (filters.industry && filters.industry !== 'ALL') {
        query = query.eq('brand_industry', filters.industry);
      }

      if (filters.dateStart) {
        query = query.gte('ad_delivery_start_time', filters.dateStart.toISOString());
      }

      if (filters.dateEnd) {
        query = query.lte('ad_delivery_start_time', filters.dateEnd.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      const ads = data as AdWithBrand[];
      const lastAd = ads[ads.length - 1];
      
      return {
        ads,
        nextCursor: ads.length === 24 ? lastAd?.ad_delivery_start_time : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: null as string | null,
    enabled: enabled,
  });
}

export function useIndustries() {
  return useQuery({
    queryKey: ['adspy-industries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adspy_industries')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as AdspyIndustry[];
    },
  });
}

export function useLanguages() {
  return useQuery({
    queryKey: ['adspy-languages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adspy_languages')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as AdspyLanguage[];
    },
  });
}
